/**
 * MentionChip Component
 *
 * Displays a rendered mention in chat messages or input area.
 * Shows as a chip with the user's name, optional avatar, and service badges.
 */

import { Chip, Avatar } from "@heroui/react";
import { Icon } from "@iconify/react";

import { ServiceBadge } from "./ServiceBadge";

interface MentionChipProps {
  /** Display name to show */
  displayName: string;
  /** Avatar URL (optional) */
  avatarUrl?: string;
  /** Available services for this identity */
  services?: string[];
  /** Whether this is an internal-only mention */
  targetMode?: "all" | "internal";
  /** Click handler (optional) */
  onClick?: () => void;
  /** Remove handler - shows X button when provided */
  onRemove?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * MentionChip renders a mention as a styled chip.
 *
 * - Regular mentions (all services): Solid primary colour
 * - Internal mentions (no notification): Bordered/outlined style
 * - With onRemove: Shows close button for removal
 *
 * @example
 * <MentionChip displayName="John Smith" avatarUrl="..." />
 * <MentionChip displayName="Jane Doe" targetMode="internal" />
 * <MentionChip displayName="Bob" onRemove={() => handleRemove()} services={["slack", "email"]} />
 */
export const MentionChip: React.FC<MentionChipProps> = ({
  displayName,
  avatarUrl,
  services = [],
  targetMode = "all",
  onClick,
  onRemove,
  className = "",
}) => {
  const isInternal = targetMode === "internal";

  return (
    <Chip
      className={`${onClick ? "cursor-pointer" : ""} ${className}`}
      color={isInternal ? "default" : "primary"}
      size="sm"
      startContent={
        <div className="flex items-center gap-1">
          {avatarUrl ? (
            <Avatar className="w-4 h-4" size="sm" src={avatarUrl} />
          ) : (
            <Icon height={14} icon="mdi:at" width={14} />
          )}
          {/* Show service badges inline */}
          {services.length > 0 && (
            <div className="flex gap-0.5 ml-1">
              {services.slice(0, 2).map((service) => (
                <ServiceBadge key={service} service={service} showLabel={false} size="xs" />
              ))}
              {services.length > 2 && (
                <span className="text-xs opacity-70">+{services.length - 2}</span>
              )}
            </div>
          )}
        </div>
      }
      variant={isInternal ? "bordered" : "solid"}
      onClick={onClick}
      onClose={onRemove}
    >
      {displayName}
    </Chip>
  );
};

export default MentionChip;
